import type { CreateOrganizationInput } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import {
  createOrganization,
  deleteOrganization,
  listOrganizations,
  listOwnMemberships,
  updateOrganization,
} from './api';

export const orgKeys = {
  /** Everything that decides what the caller can see: invalidate it after any identity mutation. */
  scope: ['org-scope'] as const,
  list: ['org-scope', 'organizations'] as const,
  memberships: (userId: string) => ['org-scope', 'memberships', userId] as const,
};

export function useOrganizations() {
  return useQuery({ queryKey: orgKeys.list, queryFn: () => listOrganizations(supabase) });
}

export function useOwnMemberships(userId: string) {
  return useQuery({
    queryKey: orgKeys.memberships(userId),
    queryFn: () => listOwnMemberships(supabase, userId),
    enabled: userId !== '',
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrganizationInput) => createOrganization(supabase, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.scope }),
  });
}

export function useUpdateOrganization(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => updateOrganization(supabase, organizationId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.scope }),
  });
}

export function useDeleteOrganization(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteOrganization(supabase, organizationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: orgKeys.scope }),
  });
}
